import * as React from "react"
import { ChartData } from "../subsection"
import * as d3 from "d3"
import { fitChartWidth, fitChartHeight, resolveChartDefaultWidth } from "./chart_size"


interface BarChartProps {
    chartData: ChartData
    compact?: boolean
    panelWidth?: number
    panelHeight?: number
    svgId?: string
}
export const GenerateBarChart = ({ chartData, compact, panelWidth, panelHeight, svgId = "bar" }: BarChartProps) => {
    const [animationComplete, setAnimationComplete] = React.useState(false)
    const [hoveredBar, setHoveredBar] = React.useState<number | null>(null)

    const w = fitChartWidth(compact, panelWidth, resolveChartDefaultWidth(compact, panelWidth, 170, 280, 560), 96, panelHeight)
    const h = fitChartHeight(compact, w, 180, 260, 400)
    const [m, bar_pad] = compact
        ? [{ top: 18, bottom: 20, left: 6, right: 28 }, 0.5]
        : [{ top: 24, bottom: 26, left: 8, right: 32 }, 0.5]
    const [bounds_w, bounds_h] = [(w - m.right - m.left), (h - m.top - m.bottom)]

    let units = "km²"
    const maxCount = Math.max(...Array.from(chartData.count.values()))

    if (maxCount < 0.05){
        units = "m²"
    }

    const data = Array.from(chartData.count, ([name, value]) => ({ name, value : units === "m²" ? value * (1000**2) : value })).sort((a, b) => b.value - a.value).slice(0, 5)

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setAnimationComplete(true)
        }, 100)
        return () => clearTimeout(timer)
    }, [])

    const xScale = d3
        .scaleLinear()
        .domain([0, Math.max(...data.map(d => d.value))])
        .range([0, bounds_w])

    const yScale = d3
        .scaleBand()
        .range([0, bounds_h])
        .domain(data.map(d => d.name))
        .padding(bar_pad)

    const rects = data.map((d, i) => {
        const color = chartData.colors?.get(d.name) ?? [0,0,0]
        const y = yScale(d.name)
        if (y === undefined) { return null }

        const barWidth = animationComplete ? xScale(d.value) : 0
        const hoverLabel = `${d.value.toFixed(2)} ${units}`
        const hoverFitsInside = barWidth > (compact ? 52 : 64)

        return (
            <g key={i}>
                <rect
                    x={0}
                    y={y}
                    width={barWidth}
                    height={yScale.bandwidth()}
                    stroke="lightgrey"
                    fill={hoveredBar === i ? `rgb(${Math.min(255, color[0] + 30)}, ${Math.min(255, color[1] + 30)}, ${Math.min(255, color[2] + 30)})` : `rgb(${color[0]}, ${color[1]}, ${color[2]})`}
                    strokeWidth={1}
                    rx={1}
                    style={{
                        transition: `width 0.8s ease-out ${i * 0.1}s, fill 0.2s ease`,
                        transformOrigin: 'left center',
                        cursor: 'pointer'
                    }}
                    onMouseEnter={() => setHoveredBar(i)}
                    onMouseLeave={() => setHoveredBar(null)}
                />
                {hoveredBar === i && (
                    <text
                        x={hoverFitsInside ? barWidth - 4 : Math.min(barWidth + 4, bounds_w - 2)}
                        y={y + yScale.bandwidth() / 2}
                        fontSize={compact ? 8 : 10}
                        fill="black"
                        textAnchor={hoverFitsInside ? "end" : "start"}
                        dominantBaseline="middle"
                        style={{
                            pointerEvents: 'none',
                            fontWeight: 'bold'
                        }}
                    >
                        {hoverLabel}
                    </text>
                )}
            </g>
        )
    })

    const grid = xScale.ticks(5).map((d, i) => (
        <g key={i}>
            <line
                x1={xScale(d)}
                x2={xScale(d)}
                y1={0}
                y2={bounds_h}
                stroke="#e0e0e0"
                strokeWidth={1}
            />
            <text
                x={xScale(d)}
                y={bounds_h + (compact ? 12 : 15)}
                fontSize={compact ? 8 : 10}
                textAnchor="middle"
                fill="black"
            >
                {d}
            </text>
        </g>
    ))

    return (
        <div style={{ maxWidth: "100%", overflow: "hidden" }}>
            <svg id={svgId} width={w} height={h} style={{ overflow: "hidden", display: "block", maxWidth: "100%", margin: "0 auto" }}>
                <g
                width={bounds_h}
                height={bounds_w}
                transform={`translate(${[m.left, m.top].join(",")})`}
                >
                    {grid}
                    {rects}
                    <text
                        x={bounds_w / 2}
                        y={bounds_h + m.bottom}
                        fontSize={compact ? 10 : 12}
                        textAnchor="middle"
                        fill="black"
                    >
                    Area ({units})
                    </text>
                </g>
            </svg>
        </div>
    )
}
